/**
 * Referral Operations Admin Page (REF-26).
 * Search by code/user, attribution inspection, commission/credit adjustments with audit reason,
 * fraud review, reward reversal, tier override with approval, referral dispute resolution.
 */

import { useState, useEffect } from 'react';
import { apiGet, apiPost, ApiError } from '../../lib/apiClient';
import { useModal } from '../../contexts/ModalContext';
import { DataTable } from '../../components/DataTable';

interface ReferralRecord {
  id: string;
  code: string;
  referrerUserId: string;
  refereeUserId?: string;
  type: string;
  status: string;
  commissionRate: number;
  qualifiedPatientCount: number;
  tier?: string;
  attributedAt?: string;
}

interface CommissionRecord {
  id: string;
  referralId: string;
  referrerUserId: string;
  caseId: string;
  paymentId: string;
  grossPayment: number;
  platformNetRevenue: number;
  referralPercentage: number;
  referralAmount: number;
  earnedAt: string;
  status: 'active' | 'expired' | 'reversed';
}

interface FraudFlaggedReferral {
  referralId: string;
  code: string;
  referrerUserId: string;
}

type ActiveTab =
  | 'search'
  | 'commissions'
  | 'credits'
  | 'fraud'
  | 'disputes'
  | 'tier-override'
  | 'kpis'
  | 'fitness-checks'
  | 'config'
  | 'attribution'
  | 'wallet-reverse'
  | 'reconcile';

interface ReferralKpis {
  totalReferrals: number;
  qualifiedReferrals: number;
  totalCommissionsPaid: number;
  activeReferrers: number;
  [key: string]: unknown;
}

interface FitnessCheck {
  name: string;
  passed: boolean;
  details?: string;
}

interface ReferralConfig {
  effective: Record<string, unknown>;
  overrides: Record<string, unknown>;
  configurablePaths: string[];
}

interface WalletReverseResult {
  reversedCount: number;
  creditIds: string[];
}

interface ReconcileResult {
  shadowLogCount: number;
  actualCommissionCount: number;
  delta: unknown[];
  recommendation: string;
}

interface AttributionRecord {
  userId: string;
  referralId?: string;
  attributedAt?: string;
  [key: string]: unknown;
}

export function ReferralOperationsPage() {
  const { alert } = useModal();
  const [activeTab, setActiveTab] = useState<ActiveTab>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ReferralRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Commission adjustment state
  const [selectedCommission, setSelectedCommission] = useState<CommissionRecord | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [auditReason, setAuditReason] = useState('');

  // Commission reverse state
  const [reverseCommissionId, setReverseCommissionId] = useState('');
  const [reverseAuditReason, setReverseAuditReason] = useState('');

  // Credit adjustment state
  const [creditId, setCreditId] = useState('');
  const [creditAdjustmentAmount, setCreditAdjustmentAmount] = useState('');
  const [creditAuditReason, setCreditAuditReason] = useState('');

  // Tier override state
  const [overrideUserId, setOverrideUserId] = useState('');
  const [newTier, setNewTier] = useState<'ambassador' | 'elite' | 'founder'>('ambassador');
  const [tierOverrideReason, setTierOverrideReason] = useState('');

  // Fraud review state
  const [fraudFlaggedUsers, setFraudFlaggedUsers] = useState<FraudFlaggedReferral[]>([]);
  const [resolvingReferralId, setResolvingReferralId] = useState<string | null>(null);
  const [resolutionChoice, setResolutionChoice] = useState<'cleared' | 'confirmed'>('cleared');
  const [fraudResolveReason, setFraudResolveReason] = useState('');

  // Disputes state
  const [disputeReferralId, setDisputeReferralId] = useState('');
  const [disputeResolution, setDisputeResolution] = useState('');
  const [disputeAuditReason, setDisputeAuditReason] = useState('');

  // KPIs state
  const [kpis, setKpis] = useState<ReferralKpis | null>(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisError, setKpisError] = useState<string | null>(null);

  // Fitness checks state
  const [fitnessChecks, setFitnessChecks] = useState<FitnessCheck[] | null>(null);
  const [fitnessLoading, setFitnessLoading] = useState(false);
  const [fitnessError, setFitnessError] = useState<string | null>(null);

  // Config state
  const [referralConfig, setReferralConfig] = useState<ReferralConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configPath, setConfigPath] = useState('');
  const [configValue, setConfigValue] = useState('');
  const [configAuditReason, setConfigAuditReason] = useState('');

  // Attribution lookup state
  const [attributionUserId, setAttributionUserId] = useState('');
  const [attribution, setAttribution] = useState<AttributionRecord | null>(null);
  const [attributionLoading, setAttributionLoading] = useState(false);
  const [attributionError, setAttributionError] = useState<string | null>(null);

  // Wallet reverse state
  const [walletReversePaymentId, setWalletReversePaymentId] = useState('');
  const [walletReverseReason, setWalletReverseReason] = useState('');
  const [walletReverseResult, setWalletReverseResult] = useState<WalletReverseResult | null>(null);
  const [walletReverseError, setWalletReverseError] = useState<string | null>(null);
  const [walletReverseLoading, setWalletReverseLoading] = useState(false);

  // Reconcile state
  const [reconcileResult, setReconcileResult] = useState<ReconcileResult | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'fraud') {
      loadFraudFlaggedUsers();
    } else if (activeTab === 'kpis') {
      loadKpis();
    } else if (activeTab === 'fitness-checks') {
      loadFitnessChecks();
    } else if (activeTab === 'config') {
      loadReferralConfig();
    } else if (activeTab === 'reconcile') {
      loadReconcile();
    }
  }, [activeTab]);

  const loadReconcile = async () => {
    setReconcileLoading(true);
    setReconcileError(null);
    try {
      const data = await apiGet<ReconcileResult>('/referral/reconcile');
      setReconcileResult(data);
    } catch (err) {
      setReconcileError(err instanceof ApiError ? err.message : 'Failed to load reconciliation');
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleWalletReverse = async () => {
    if (!walletReversePaymentId.trim()) return;
    setWalletReverseLoading(true);
    setWalletReverseError(null);
    setWalletReverseResult(null);
    try {
      const result = await apiPost<WalletReverseResult>('/referral/wallet/reverse', {
        paymentId: walletReversePaymentId,
        reason: walletReverseReason || undefined,
      });
      setWalletReverseResult(result);
      await alert('Wallet credits reversed');
      setWalletReversePaymentId('');
      setWalletReverseReason('');
    } catch (err) {
      setWalletReverseError(err instanceof ApiError ? err.message : 'Wallet reversal failed');
    } finally {
      setWalletReverseLoading(false);
    }
  };

  const loadKpis = async () => {
    setKpisLoading(true);
    setKpisError(null);
    try {
      const data = await apiGet<ReferralKpis>('/referral/admin/kpis');
      setKpis(data);
    } catch (err) {
      setKpisError(err instanceof ApiError ? err.message : 'Failed to load KPIs');
    } finally {
      setKpisLoading(false);
    }
  };

  const loadFitnessChecks = async () => {
    setFitnessLoading(true);
    setFitnessError(null);
    try {
      const data = await apiGet<FitnessCheck[]>('/referral/admin/fitness-checks');
      setFitnessChecks(data);
    } catch (err) {
      setFitnessError(err instanceof ApiError ? err.message : 'Failed to load fitness checks');
    } finally {
      setFitnessLoading(false);
    }
  };

  const loadReferralConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const data = await apiGet<ReferralConfig>('/referral/admin/config');
      setReferralConfig(data);
    } catch (err) {
      setConfigError(err instanceof ApiError ? err.message : 'Failed to load referral config');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    if (!configPath.trim() || !configAuditReason.trim()) return;
    setConfigError(null);
    try {
      let value: unknown = configValue;
      try {
        value = JSON.parse(configValue);
      } catch {
        // not valid JSON — send as a raw string
      }
      await apiPost('/referral/admin/config', { path: configPath, value, auditReason: configAuditReason });
      await alert('Config updated successfully');
      setConfigPath('');
      setConfigValue('');
      setConfigAuditReason('');
      loadReferralConfig();
    } catch (err) {
      setConfigError(err instanceof ApiError ? err.message : 'Config update failed');
    }
  };

  const handleAttributionLookup = async () => {
    if (!attributionUserId.trim()) return;
    setAttributionLoading(true);
    setAttributionError(null);
    setAttribution(null);
    try {
      const data = await apiGet<AttributionRecord>(`/referral/admin/attributions/${encodeURIComponent(attributionUserId.trim())}`);
      setAttribution(data);
    } catch (err) {
      setAttributionError(err instanceof ApiError ? err.message : 'Attribution lookup failed');
    } finally {
      setAttributionLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const results = await apiGet<ReferralRecord[]>(`/referral/admin/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustCommission = async () => {
    if (!selectedCommission || !auditReason.trim()) return;

    try {
      await apiPost('/referral/admin/commissions/adjust', {
        commissionId: selectedCommission.id,
        adjustmentAmount: Number(adjustmentAmount),
        auditReason,
      });
      await alert('Commission adjusted successfully');
      setSelectedCommission(null);
      setAdjustmentAmount('');
      setAuditReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Adjustment failed');
    }
  };

  const handleReverseCommission = async () => {
    if (!reverseCommissionId.trim() || !reverseAuditReason.trim()) return;

    try {
      await apiPost('/referral/admin/commissions/reverse', {
        commissionId: reverseCommissionId,
        auditReason: reverseAuditReason,
      });
      await alert('Commission reversed successfully');
      setReverseCommissionId('');
      setReverseAuditReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reversal failed');
    }
  };

  const handleAdjustCredit = async () => {
    if (!creditId.trim() || !creditAuditReason.trim()) return;

    try {
      await apiPost('/referral/admin/credits/adjust', {
        creditId,
        adjustmentAmount: Number(creditAdjustmentAmount),
        auditReason: creditAuditReason,
      });
      await alert('Credit adjusted successfully');
      setCreditId('');
      setCreditAdjustmentAmount('');
      setCreditAuditReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Credit adjustment failed');
    }
  };

  const handleOverrideTier = async () => {
    if (!overrideUserId.trim() || !tierOverrideReason.trim()) return;

    try {
      await apiPost('/referral/admin/tier-override', {
        userId: overrideUserId,
        newTier,
        auditReason: tierOverrideReason,
      });
      await alert('Tier override successful');
      setOverrideUserId('');
      setNewTier('ambassador');
      setTierOverrideReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Tier override failed');
    }
  };

  const handleResolveFraudFlag = async () => {
    if (!resolvingReferralId || !fraudResolveReason.trim()) return;

    try {
      await apiPost('/referral/admin/fraud-flags/resolve', {
        referralId: resolvingReferralId,
        resolution: resolutionChoice,
        auditReason: fraudResolveReason,
      });
      await alert('Fraud flag resolved');
      setResolvingReferralId(null);
      setResolutionChoice('cleared');
      setFraudResolveReason('');
      loadFraudFlaggedUsers();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Fraud resolution failed');
    }
  };

  const handleResolveDispute = async () => {
    if (!disputeReferralId.trim() || !disputeResolution.trim() || !disputeAuditReason.trim()) return;

    try {
      await apiPost('/referral/admin/disputes/resolve', {
        referralId: disputeReferralId,
        resolution: disputeResolution,
        auditReason: disputeAuditReason,
      });
      await alert('Dispute resolved');
      setDisputeReferralId('');
      setDisputeResolution('');
      setDisputeAuditReason('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Dispute resolution failed');
    }
  };

  const loadFraudFlaggedUsers = async () => {
    try {
      const users = await apiGet<FraudFlaggedReferral[]>('/referral/admin/fraud-flags');
      setFraudFlaggedUsers(users);
    } catch (err) {
      console.error('Failed to load fraud flagged users:', err);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Referral Operations</h1>
          <p>Manage referral codes, commissions, credits, and fraud review. All changes require audit reason.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {(['search', 'commissions', 'credits', 'fraud', 'disputes', 'tier-override', 'kpis', 'fitness-checks', 'config', 'attribution', 'wallet-reverse', 'reconcile'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
          >
            {tab === 'tier-override'
              ? 'Tier Override'
              : tab === 'kpis'
                ? 'KPIs'
                : tab === 'fitness-checks'
                  ? 'Fitness Checks'
                  : tab === 'attribution'
                    ? 'Attribution Lookup'
                    : tab === 'wallet-reverse'
                      ? 'Wallet Reverse'
                      : tab === 'reconcile'
                        ? 'Reconcile'
                        : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Search Referrals</h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Enter referral code (MLR-*) or user ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ flex: 1 }}
              />
              <button onClick={handleSearch} disabled={loading} className="btn btn-primary">
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {error && <p className="auth-error mt-2" role="alert">{error}</p>}

            {searchResults.length > 0 && (
              <DataTable
                id="referral-search"
                rows={searchResults}
                noun="referrals"
                getRowKey={(ref) => ref.code}
                columns={[
                  { key: 'code', header: 'Code', value: (ref) => ref.code },
                  { key: 'referrer', header: 'Referrer', value: (ref) => ref.referrerUserId },
                  { key: 'type', header: 'Type', value: (ref) => ref.type, filter: 'select' },
                  { key: 'status', header: 'Status', value: (ref) => ref.status, filter: 'select' },
                  { key: 'tier', header: 'Tier', value: (ref) => ref.tier || '-', filter: 'select' },
                  {
                    key: 'qualified',
                    header: 'Qualified',
                    value: (ref) => ref.qualifiedPatientCount,
                    align: 'right',
                  },
                ]}
              />
            )}
          </div>
        </div>
      )}

      {/* Commissions Tab */}
      {activeTab === 'commissions' && (
        <div className="card-grid">
          <div className="form-card">
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Adjust Commission</h2>
            <p className="form-hint">Select a commission to adjust. All adjustments require an audit reason.</p>

            {selectedCommission ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px' }}>
                  <p><strong>Commission ID:</strong> {selectedCommission.id}</p>
                  <p><strong>Payment:</strong> {selectedCommission.paymentId}</p>
                  <p><strong>Current Amount:</strong> ${(selectedCommission.referralAmount / 100).toFixed(2)}</p>
                  <p><strong>Status:</strong> {selectedCommission.status}</p>
                </div>

                <label htmlFor="adjustmentAmount">Adjustment Amount (minor units — positive to add, negative to subtract)</label>
                <input
                  id="adjustmentAmount"
                  type="number"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(e.target.value)}
                />

                <label htmlFor="auditReason">Audit Reason (required)</label>
                <textarea
                  id="auditReason"
                  required
                  rows={3}
                  value={auditReason}
                  onChange={(e) => setAuditReason(e.target.value)}
                  placeholder="e.g. fraud_review_adjustment"
                />

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={handleAdjustCommission} className="btn btn-primary">Apply Adjustment</button>
                  <button onClick={() => setSelectedCommission(null)} className="btn">Cancel</button>
                </div>
              </div>
            ) : (
              <p className="form-hint">Select a commission from the search results to adjust.</p>
            )}
          </div>

          <div className="form-card">
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Reverse Commission</h2>
            <p className="form-hint">Reverse a commission. Requires audit reason.</p>

            <label htmlFor="reverseCommissionId">Commission ID</label>
            <input
              id="reverseCommissionId"
              type="text"
              value={reverseCommissionId}
              onChange={(e) => setReverseCommissionId(e.target.value)}
              placeholder="Commission ID to reverse"
            />

            <label htmlFor="reverseAuditReason">Audit Reason (required)</label>
            <textarea
              id="reverseAuditReason"
              required
              rows={3}
              value={reverseAuditReason}
              onChange={(e) => setReverseAuditReason(e.target.value)}
              placeholder="e.g. fraud_confirmed_reversal"
            />

            <button onClick={handleReverseCommission} className="btn btn-danger">Reverse Commission</button>
          </div>
        </div>
      )}

      {/* Credits Tab */}
      {activeTab === 'credits' && (
        <div className="form-card">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Credit Management</h2>
          <p className="form-hint">Adjust credit balances by ID. Requires audit reason.</p>

          <label htmlFor="creditId">Credit ID</label>
          <input
            id="creditId"
            type="text"
            value={creditId}
            onChange={(e) => setCreditId(e.target.value)}
            placeholder="Credit ID to adjust"
          />

          <label htmlFor="creditAdjustmentAmount">Adjustment Amount (minor units — positive to add, negative to subtract)</label>
          <input
            id="creditAdjustmentAmount"
            type="number"
            value={creditAdjustmentAmount}
            onChange={(e) => setCreditAdjustmentAmount(e.target.value)}
          />

          <label htmlFor="creditAuditReason">Audit Reason (required)</label>
          <textarea
            id="creditAuditReason"
            required
            rows={3}
            value={creditAuditReason}
            onChange={(e) => setCreditAuditReason(e.target.value)}
            placeholder="e.g. manual_credit_adjustment"
          />

          <button onClick={handleAdjustCredit} className="btn btn-primary">Apply Credit Adjustment</button>
        </div>
      )}

      {/* Fraud Tab */}
      {activeTab === 'fraud' && (
        <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Fraud Review Queue</h2>
          {fraudFlaggedUsers.length > 0 ? (
            <DataTable
              id="referral-fraud"
              rows={fraudFlaggedUsers}
              empty="No referrals are currently flagged for fraud review."
              noun="flags"
              getRowKey={(flag) => flag.referralId}
              columns={[
                { key: 'referralId', header: 'Referral ID', value: (flag) => flag.referralId },
                { key: 'code', header: 'Code', value: (flag) => flag.code },
                { key: 'referrer', header: 'Referrer User ID', value: (flag) => flag.referrerUserId },
                {
                  key: 'actions',
                  header: 'Actions',
                  render: (flag) => (
                    <>
                  {resolvingReferralId === flag.referralId ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <select
                        value={resolutionChoice}
                        onChange={(e) => setResolutionChoice(e.target.value as 'cleared' | 'confirmed')}
                      >
                        <option value="cleared">Cleared</option>
                        <option value="confirmed">Confirmed Fraud</option>
                      </select>
                      <label htmlFor={`fraud-resolve-${flag.referralId}`}>Audit Reason (required)</label>
                      <textarea
                        id={`fraud-resolve-${flag.referralId}`}
                        required
                        rows={2}
                        value={fraudResolveReason}
                        onChange={(e) => setFraudResolveReason(e.target.value)}
                        placeholder="Resolution reason"
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={handleResolveFraudFlag} className="btn btn-primary">Submit</button>
                        <button onClick={() => setResolvingReferralId(null)} className="btn">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setResolvingReferralId(flag.referralId); setResolutionChoice('cleared'); setFraudResolveReason(''); }} className="btn btn-primary">Review</button>
                  )}

                    </>
                  ),
                },
              ]}
            />
          ) : (
            <p className="empty-state">No fraud-flagged referrals at this time.</p>
          )}
        </div>
      )}

      {/* Disputes Tab */}
      {activeTab === 'disputes' && (
        <div className="form-card">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Dispute Resolution</h2>
          <p className="form-hint">Resolve a referral dispute. Requires audit reason.</p>

          <label htmlFor="disputeReferralId">Referral ID</label>
          <input
            id="disputeReferralId"
            type="text"
            value={disputeReferralId}
            onChange={(e) => setDisputeReferralId(e.target.value)}
            placeholder="Referral ID to resolve"
          />

          <label htmlFor="disputeResolution">Resolution</label>
          <input
            id="disputeResolution"
            type="text"
            value={disputeResolution}
            onChange={(e) => setDisputeResolution(e.target.value)}
            placeholder="e.g. resolved, escalated, withdrawn"
          />

          <label htmlFor="disputeAuditReason">Audit Reason (required)</label>
          <textarea
            id="disputeAuditReason"
            required
            rows={3}
            value={disputeAuditReason}
            onChange={(e) => setDisputeAuditReason(e.target.value)}
            placeholder="e.g. patient_verified, code_invalidated"
          />

          <button onClick={handleResolveDispute} className="btn btn-primary">Resolve Dispute</button>
        </div>
      )}

      {/* Tier Override Tab */}
      {activeTab === 'tier-override' && (
      <div className="form-card m-t-20">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Tier Override (Requires Admin Approval)</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label htmlFor="overrideUserId">User ID</label>
          <input
            id="overrideUserId"
            type="text"
            value={overrideUserId}
            onChange={(e) => setOverrideUserId(e.target.value)}
          />

          <label htmlFor="newTier">New Tier</label>
          <select
            id="newTier"
            value={newTier}
            onChange={(e) => setNewTier(e.target.value as typeof newTier)}
          >
            <option value="ambassador">Ambassador (15%)</option>
            <option value="elite">Elite (17%)</option>
            <option value="founder">Founder Circle (20%)</option>
          </select>

          <label htmlFor="tierOverrideReason">Audit Reason (required)</label>
          <textarea
            id="tierOverrideReason"
            required
            rows={3}
            value={tierOverrideReason}
            onChange={(e) => setTierOverrideReason(e.target.value)}
            placeholder="e.g. manual_review_approved"
          />

          <button onClick={handleOverrideTier} className="btn btn-primary">Apply Tier Override</button>
        </div>
      </div>
      )}

      {/* KPIs Tab */}
      {activeTab === 'kpis' && (
        <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Referral Growth KPIs</h2>
          {kpisError && <p className="auth-error mt-2" role="alert">{kpisError}</p>}
          {kpisLoading && !kpis ? (
            <div className="empty-state">Loading…</div>
          ) : !kpis ? (
            <div className="empty-state">No KPI data.</div>
          ) : (
            <DataTable
              rows={Object.entries(kpis).map(([key, value]) => ({ key, value }))}
              getRowKey={(r) => r.key}
              controls={false}
              pageSize={null}
              columns={[
                { key: 'metric', header: 'Metric', value: (r) => r.key },
                {
                  key: 'value',
                  header: 'Value',
                  value: (r) => (typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value)),
                },
              ]}
            />
          )}
        </div>
      )}

      {/* Fitness Checks Tab */}
      {activeTab === 'fitness-checks' && (
        <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Referral System Fitness Checks</h2>
          {fitnessError && <p className="auth-error mt-2" role="alert">{fitnessError}</p>}
          {fitnessLoading && !fitnessChecks ? (
            <div className="empty-state">Loading…</div>
          ) : !fitnessChecks || fitnessChecks.length === 0 ? (
            <div className="empty-state">No fitness checks reported.</div>
          ) : (
            <DataTable
              id="referral-fitness"
              rows={fitnessChecks}
              loading={fitnessLoading && !fitnessChecks}
              empty="No fitness checks reported."
              noun="checks"
              getRowKey={(check) => check.name}
              columns={[
                { key: 'check', header: 'Check', value: (check) => check.name },
                {
                  key: 'status',
                  header: 'Status',
                  value: (check) => (check.passed ? 'Pass' : 'Fail'),
                  filter: 'select',
                  render: (check) => (
                    <span style={{ color: check.passed ? 'var(--success)' : 'var(--danger-bold)', fontWeight: 600 }}>
                      {check.passed ? 'Pass' : 'Fail'}
                    </span>
                  ),
                },
                { key: 'details', header: 'Details', value: (check) => check.details || '\u2014' },
              ]}
            />
          )}
        </div>
      )}

      {/* Config Tab */}
      {activeTab === 'config' && (
        <div className="card-grid">
          <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Effective Referral Config</h2>
            {configError && <p className="auth-error mt-2" role="alert">{configError}</p>}
            {configLoading && !referralConfig ? (
              <div className="empty-state">Loading…</div>
            ) : !referralConfig ? (
              <div className="empty-state">No config data.</div>
            ) : (
              <>
                <p className="form-hint">Configurable paths: {referralConfig.configurablePaths.join(', ') || 'none'}</p>
                <DataTable
                  id="referral-config"
                  rows={Object.entries(referralConfig.effective).map(([key, value]) => ({ key, value }))}
                  getRowKey={(r) => r.key}
                  empty="No config data."
                  noun="paths"
                  columns={[
                    { key: 'path', header: 'Path', value: (r) => r.key },
                    {
                      key: 'value',
                      header: 'Effective value',
                      value: (r) => (typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value)),
                    },
                    {
                      key: 'override',
                      header: 'Override?',
                      value: (r) => (r.key in referralConfig.overrides ? 'Yes' : 'No'),
                      filter: 'select',
                    },
                  ]}
                />
              </>
            )}
          </div>

          <div className="form-card">
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Update Config</h2>
            <p className="form-hint">Set a config override. Requires audit reason.</p>

            <label htmlFor="configPath">Path</label>
            <input
              id="configPath"
              type="text"
              value={configPath}
              onChange={(e) => setConfigPath(e.target.value)}
              placeholder="Config path to override"
            />

            <label htmlFor="configValue">Value (JSON or plain string)</label>
            <input
              id="configValue"
              type="text"
              value={configValue}
              onChange={(e) => setConfigValue(e.target.value)}
              placeholder='e.g. 0.15 or "elite" or {"key":"value"}'
            />

            <label htmlFor="configAuditReason">Audit Reason (required)</label>
            <textarea
              id="configAuditReason"
              required
              rows={3}
              value={configAuditReason}
              onChange={(e) => setConfigAuditReason(e.target.value)}
              placeholder="e.g. adjust_commission_rate"
            />

            <button onClick={handleUpdateConfig} className="btn btn-primary">Save Config</button>
          </div>
        </div>
      )}

      {/* Attribution Lookup Tab */}
      {activeTab === 'attribution' && (
        <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Attribution Lookup</h2>
          <p className="form-hint">Look up which referral a user is attributed to.</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="User ID"
              value={attributionUserId}
              onChange={(e) => setAttributionUserId(e.target.value)}
              style={{ flex: 1 }}
            />
            <button onClick={handleAttributionLookup} disabled={attributionLoading} className="btn btn-primary">
              {attributionLoading ? 'Looking up…' : 'Look up'}
            </button>
          </div>

          {attributionError && <p className="auth-error mt-2" role="alert">{attributionError}</p>}

          {attribution && (
            <DataTable
              rows={Object.entries(attribution).map(([key, value]) => ({ key, value }))}
              getRowKey={(r) => r.key}
              controls={false}
              pageSize={null}
              columns={[
                { key: 'path', header: 'Field', value: (r) => r.key },
                {
                  key: 'value',
                  header: 'Value',
                  value: (r) => (typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value)),
                },
              ]}
            />
          )}
        </div>
      )}

      {/* Wallet Reverse Tab */}
      {activeTab === 'wallet-reverse' && (
        <div className="form-card">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Reverse Wallet Credits</h2>
          <p className="form-hint">
            Reverses referral wallet credits tied to a payment (e.g. after a refund). Reason is optional but recommended.
          </p>

          <label htmlFor="walletReversePaymentId">Payment ID</label>
          <input
            id="walletReversePaymentId"
            type="text"
            value={walletReversePaymentId}
            onChange={(e) => setWalletReversePaymentId(e.target.value)}
            placeholder="Payment ID to reverse credits for"
          />

          <label htmlFor="walletReverseReason">Reason (optional)</label>
          <textarea
            id="walletReverseReason"
            rows={3}
            value={walletReverseReason}
            onChange={(e) => setWalletReverseReason(e.target.value)}
            placeholder="e.g. payment_refunded"
          />

          {walletReverseError && <p className="auth-error mt-2" role="alert">{walletReverseError}</p>}

          <button onClick={handleWalletReverse} disabled={walletReverseLoading || !walletReversePaymentId.trim()} className="btn btn-danger">
            {walletReverseLoading ? 'Reversing…' : 'Reverse Wallet Credits'}
          </button>

          {walletReverseResult && (
            <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px', marginTop: '1rem' }}>
              <p><strong>Reversed count:</strong> {walletReverseResult.reversedCount}</p>
              <p><strong>Credit IDs:</strong> {walletReverseResult.creditIds.join(', ') || '—'}</p>
            </div>
          )}
        </div>
      )}

      {/* Reconcile Tab */}
      {activeTab === 'reconcile' && (
        <div className="form-card" style={{ maxWidth: 'none', width: '100%' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Shadow-Mode Reconciliation</h2>
          {reconcileError && <p className="auth-error mt-2" role="alert">{reconcileError}</p>}
          {reconcileLoading && !reconcileResult ? (
            <div className="empty-state">Loading…</div>
          ) : !reconcileResult ? (
            <div className="empty-state">No reconciliation data.</div>
          ) : (
            <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px' }}>
              <p><strong>Shadow log count:</strong> {reconcileResult.shadowLogCount}</p>
              <p><strong>Actual commission count:</strong> {reconcileResult.actualCommissionCount}</p>
              <p><strong>Delta entries:</strong> {reconcileResult.delta?.length ?? 0}</p>
              <p><strong>Recommendation:</strong> {reconcileResult.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
